export const ACCESS_BLOCKED_ROUTE = "/access-blocked"

export const ACCESS_BLOCKED_CODES = new Set([
  "BILLING_REQUIRED",
  "ENTITLEMENT_LIMIT",
  "DS_ADDON_DISABLED",
  "SUBSCRIPTION_CANCELED",
  "SUBSCRIPTION_CANCELLED",
  "SUBSCRIPTION_PAST_DUE",
  "SUBSCRIPTION_EXPIRED",
  "SUBSCRIPTION_INACTIVE",
  "SUBSCRIPTION_UNPAID",
])

export type AccessBlockedDetails = {
  code: string
  title: string
  message: string
  billingUrl?: string
  status?: number | string
  returnTo?: string
}

const ERROR_DETAILS: Record<string, { title: string; message: string }> = {
  BILLING_REQUIRED: {
    title: "Billing required",
    message: "DreamSender billing is required before this action can continue.",
  },
  ENTITLEMENT_LIMIT: {
    title: "Entitlement limit reached",
    message:
      "Your current DreamSender entitlement does not include enough capacity for this action.",
  },
  DS_ADDON_DISABLED: {
    title: "DreamSender add-on disabled",
    message:
      "The DreamSender add-on is disabled for this subscription. Turn it on in billing to continue.",
  },
  SUBSCRIPTION_CANCELED: {
    title: "Subscription canceled",
    message: "Reactivate the subscription before continuing.",
  },
  SUBSCRIPTION_CANCELLED: {
    title: "Subscription canceled",
    message: "Reactivate the subscription before continuing.",
  },
  SUBSCRIPTION_PAST_DUE: {
    title: "Subscription past due",
    message: "Update billing details before continuing.",
  },
  SUBSCRIPTION_EXPIRED: {
    title: "Subscription expired",
    message: "Renew the subscription before continuing.",
  },
  SUBSCRIPTION_INACTIVE: {
    title: "Subscription inactive",
    message: "Activate the subscription before continuing.",
  },
  SUBSCRIPTION_UNPAID: {
    title: "Subscription unpaid",
    message: "Resolve the unpaid subscription before continuing.",
  },
}

const isRecord = (value: unknown): value is Record<string, any> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

const DEFAULT_INBOXING_BILLING_URL = "https://app.inboxing.com/billing"

const BILLING_URL_KEYS = [
  "billingUrl",
  "inboxingBillingUrl",
  "subscriptionUrl",
  "inboxingSubscriptionUrl",
  "payUrl",
  "paymentUrl",
  "checkoutUrl",
  "billingPortalUrl",
  "customerPortalUrl",
  "portalUrl",
  "manageBillingUrl",
  "manageSubscriptionUrl",
]

const BILLING_URL_SOURCE_KEYS = [
  "billing",
  "config",
  "details",
  "entitlement",
  "entitlements",
  "error",
  "inboxing",
  "inboxingConfig",
  "subscription",
]

export const safeHttpUrl = (value?: unknown) => {
  if (value === undefined || value === null) return undefined

  const raw = String(value).trim()
  if (!raw) return undefined

  try {
    const url = new URL(raw)
    if (!["http:", "https:"].includes(url.protocol)) return undefined
    if (url.username || url.password) return undefined
    return url.toString()
  } catch {
    return undefined
  }
}

const billingUrlFromRecord = (
  source: Record<string, any>,
  depth = 0,
): string | undefined => {
  for (const key of BILLING_URL_KEYS) {
    const url = safeHttpUrl(source[key])
    if (url) return url
  }

  if (depth >= 2) return undefined

  for (const key of BILLING_URL_SOURCE_KEYS) {
    const nested = source[key]
    if (!isRecord(nested)) continue

    const url = billingUrlFromRecord(nested, depth + 1)
    if (url) return url
  }

  return undefined
}

export const inboxingBillingFallbackUrl = () =>
  safeHttpUrl(import.meta.env.VITE_INBOXING_BILLING_URL) ||
  DEFAULT_INBOXING_BILLING_URL

export const inboxingBillingUrlFromData = (
  data: unknown,
  options: { fallback?: boolean } = {},
) => {
  const provided = isRecord(data)
    ? billingUrlFromRecord(data)
    : safeHttpUrl(data)

  return (
    provided || (options.fallback ? inboxingBillingFallbackUrl() : undefined)
  )
}

export const normalizeAccessBlockedCode = (value?: unknown) =>
  value === undefined || value === null
    ? undefined
    : String(value).trim().toUpperCase()

const humanize = (value: string) =>
  value
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase())

export const isAccessBlockedCode = (value?: unknown) => {
  const code = normalizeAccessBlockedCode(value)
  return Boolean(
    code &&
      (ACCESS_BLOCKED_CODES.has(code) || code.startsWith("SUBSCRIPTION_")),
  )
}

export const accessBlockedCodeFromData = (data: unknown) => {
  if (!isRecord(data)) return undefined

  const candidates = [
    data.error?.code,
    data.code,
    data.details?.code,
    data.errorCode,
    data.blockedCode,
    data.blockCode,
    data.reason,
  ]

  return candidates.map(normalizeAccessBlockedCode).find(Boolean)
}

export const accessBlockedMessageFromData = (data: unknown) => {
  if (!isRecord(data)) return undefined

  const message =
    data.message ||
    data.error?.message ||
    data.details?.message ||
    data.description
  return message === undefined || message === null || message === ""
    ? undefined
    : String(message)
}

export const accessBlockedDetailsFromData = (
  data: unknown,
  status?: number | string,
): AccessBlockedDetails | undefined => {
  const code =
    accessBlockedCodeFromData(data) ||
    (status === 402 ? "BILLING_REQUIRED" : "")

  if (!code || (!isAccessBlockedCode(code) && status !== 402)) {
    return undefined
  }

  const details = ERROR_DETAILS[code]
  const message = accessBlockedMessageFromData(data)
  const billingUrl = inboxingBillingUrlFromData(data)

  return {
    code,
    title: details?.title || humanize(code),
    message:
      message ||
      details?.message ||
      "Your account does not currently have access to this DreamSender capability.",
    ...(billingUrl ? { billingUrl } : {}),
    status,
  }
}

export const isAccessBlockedErrorData = (
  data: unknown,
  status?: number | string,
) => Boolean(accessBlockedDetailsFromData(data, status))

export const accessBlockedSearch = (details: AccessBlockedDetails) => {
  const params = new URLSearchParams()
  params.set("code", details.code)
  params.set("title", details.title)
  params.set("message", details.message)
  const billingUrl = safeHttpUrl(details.billingUrl)
  if (billingUrl) params.set("billingUrl", billingUrl)
  if (details.status !== undefined) params.set("status", String(details.status))
  if (details.returnTo) params.set("returnTo", details.returnTo)
  return params.toString()
}
